import { X, GripVertical, Play, Trash2, Music } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QueueTrack {
  id: string;
  title: string;
  artist: string | null;
  coverImage: string | null;
  voicePart?: string;
}

interface QueuePanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentTrack: QueueTrack | null;
  queue: QueueTrack[];
  onPlayFromQueue: (index: number) => void;
  onRemoveFromQueue: (index: number) => void;
  onReorderQueue?: (fromIndex: number, toIndex: number) => void;
}

function TrackItem({
  track,
  isPlaying = false,
  showGrip = false,
  onPlay,
  onRemove,
}: {
  track: QueueTrack;
  isPlaying?: boolean;
  showGrip?: boolean;
  onPlay?: () => void;
  onRemove?: () => void;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 p-2 rounded-lg group transition-colors',
        isPlaying ? 'bg-zzing-purple-500/20' : 'hover:bg-bg-elevated-hover'
      )}
    >
      {showGrip && (
        <GripVertical className="w-4 h-4 text-text-muted cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" />
      )}

      <div className="relative w-10 h-10 rounded overflow-hidden bg-bg-elevated flex-shrink-0">
        {track.coverImage ? (
          <img
            src={track.coverImage}
            alt={track.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="w-5 h-5 text-text-muted" />
          </div>
        )}
        {onPlay && (
          <button
            onClick={onPlay}
            className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Play className="w-5 h-5 text-white" fill="white" />
          </button>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm font-medium truncate',
          isPlaying ? 'text-zzing-purple-400' : 'text-text-primary'
        )}>
          {track.title}
        </p>
        <p className="text-xs text-text-secondary truncate">
          {track.artist || 'Onbekend'}
          {track.voicePart && ` • ${track.voicePart}`}
        </p>
      </div>

      {onRemove && (
        <button
          onClick={onRemove}
          className="p-1.5 text-text-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function QueuePanel({
  isOpen,
  onClose,
  currentTrack,
  queue,
  onPlayFromQueue,
  onRemoveFromQueue,
}: QueuePanelProps) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed right-0 top-0 h-full w-80 bg-bg-elevated border-l border-subtle z-50',
          'transform transition-transform duration-300 ease-out',
          'flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-subtle">
          <h2 className="text-lg font-semibold text-text-primary">Wachtrij</h2>
          <button
            onClick={onClose}
            className="p-2 text-text-muted hover:text-text-primary rounded-lg hover:bg-bg-elevated-hover transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Now Playing */}
          {currentTrack && (
            <section>
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                Nu aan het spelen
              </h3>
              <TrackItem track={currentTrack} isPlaying />
            </section>
          )}

          {/* Queue */}
          {queue.length > 0 ? (
            <section>
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                Hierna ({queue.length})
              </h3>
              <div className="space-y-1">
                {queue.map((track, index) => (
                  <TrackItem
                    key={`${track.id}-${index}`}
                    track={track}
                    showGrip
                    onPlay={() => onPlayFromQueue(index)}
                    onRemove={() => onRemoveFromQueue(index)}
                  />
                ))}
              </div>
            </section>
          ) : (
            !currentTrack && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Music className="w-12 h-12 text-text-muted mb-4" />
                <p className="text-text-secondary">De wachtrij is leeg</p>
                <p className="text-sm text-text-muted mt-1">
                  Voeg nummers toe via het contextmenu
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
}
